import { z } from 'zod'
import { registrationFormSchema } from '@integration-layer/schemas/userSchemas'
import type { Brand } from '@integration-layer/index'

/**
 * Composable for managing user registration process.
 *
 * @returns {Object} Registration management utilities and state.
 * @property {string} REGISTRATION_FORM_CODE_ID - Unique identifier for registration code form.
 * @property {string} REGISTRATION_FORM_PERSONAL_INFO_ID - Unique identifier for personal info form.
 * @property {Ref<boolean>} registrationSuccessful - Indicates successful registration.
 * @property {Ref<RegistrationUser>} registrationUser - Reactive user registration data.
 * @property {Function} cleanRegistrationData - Resets registration data.
 * @property {Function} registerUser - Submits user registration.
 * @property {Ref<boolean>} isOtpValid - Indicates OTP validation status.
 * @property {Ref<string>} salesforceId - User's Salesforce ID.
 * @property {boolean} isSignupPage - Indicates if current route is signup page.
 * @property {Function} userAlreadyOnSf - Checks if user exists on Salesforce.
 *
 * @example
 * const { registerUser, registrationUser, registrationSuccessful } = useRegistration()
 */
export const useRegistration = () => {
  const { emailExists } = useAuth0()

  // Registration form identifiers
  const REGISTRATION_FORM_CODE_ID = 'registration-form-code-id'
  const REGISTRATION_FORM_PERSONAL_INFO_ID =
    'registration-form-personal-info-id'

  const { signupPage } = useRouteHelper()
  const { currentWebsite, isBrand } = useWebsite()
  const route = useRoute()
  const { formatDate, encodeDate } = useDateOfBirth()
  const config = useAppConfig()
  const country = config.currentCountry as string
  const language = config.currentLanguage

  // Registration state constants
  const REGISTRATION_SUCCESSFUL = 'registration-successful'
  const REGISTRATION_USER = 'registrationUser'

  const brandMapping: { [key: string]: string } = brandAcronymMap

  /**
   * Computes brand preferences based on current website.
   *
   * @type {ComputedRef<string[]>}
   */
  const brandPreference = computed(() => {
    if (isBrand(currentWebsite.value)) {
      return [brandMapping[currentWebsite.value] || currentWebsite.value]
    } else {
      return ['GA', 'EA', 'E7', 'AX']
    }
  })

  /**
   * Capitalizes the first letter of a string.
   *
   * @param {string} str - Input string to capitalize.
   * @returns {string} Capitalized string.
   */
  const capitalize = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()

  // State management for registration process
  const salesforceId = useState('user_registration_salesforce_ID', () => '')
  const isOtpValid = useState<boolean>(
    'use_registration_otp_validity',
    () => false
  )

  /**
   * Reactive state for user registration data.
   *
   * @type {Ref<RegistrationUser>}
   */
  const registrationUser = useState<RegistrationUser>(
    REGISTRATION_USER,
    () => ({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      gender: undefined,
      date_of_birth: '',
      prefix: '',
      phone_number: '',
      profiling_consent: undefined,
      channels_preferences: [],
      marketing_authorization: undefined,
      brand_preferences: brandPreference.value ?? [],
      verificationCode: '',
      country: country,
      language: language,
    })
  )

  // Salesforce ID request tracking
  // on otp validity, fetch or create a CRM account

  const sfIdRequested = useState('requested_sfId', () => false)

  // Watchers for handling registration state
  watch(registrationUser, (n, o) => {
    if (n.email !== o.email) {
      sfIdRequested.value = false
    }
  })
  watch(
    isOtpValid,
    async (isValid, oldIsValid) => {
      if (isValid && isValid !== oldIsValid && !sfIdRequested.value) {
        sfIdRequested.value = true

        const { salesforceId: sfId, user } = await $fetch(
          '/api/auth0/getSfUser',
          {
            method: 'POST',
            body: {
              email: registrationUser.value.email,
            },
          }
        )
        salesforceId.value = sfId ?? ''
        if (user && typeof user !== 'boolean') {
          if (user.gender) registrationUser.value.gender = user.gender
          if (user.first_name)
            registrationUser.value.first_name = capitalize(user.first_name)
          if (user.last_name)
            registrationUser.value.last_name = capitalize(user.last_name)
          if (user.date_of_birth) {
            const formattedDate = formatDate(user.date_of_birth)
            registrationUser.value.date_of_birth = formattedDate
          }
          if (user.phone_number)
            registrationUser.value.phone_number = user.phone_number
          if (user.prefix) registrationUser.value.prefix = user.prefix
          if (user.marketing_authorization !== undefined) {
            // Explicit check for undefined to allow false values
            registrationUser.value.marketing_authorization =
              user.marketing_authorization
          }
          if (user.profiling_consent !== undefined) {
            registrationUser.value.profiling_consent = user.profiling_consent
          }
          if (user.channels_preferences) {
            registrationUser.value.channels_preferences =
              user.channels_preferences
          }
        }
      }
    },
    { immediate: true }
  )

  const registrationSuccessful = useState<boolean>(
    REGISTRATION_SUCCESSFUL,
    () => false
  )

  /**
   * Resets registration data to initial state.
   */
  const cleanRegistrationData = () => {
    registrationUser.value = {
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      gender: undefined,
      date_of_birth: '',
      prefix: '',
      phone_number: '',
      profiling_consent: undefined,
      channels_preferences: [],
      marketing_authorization: undefined,
      brand_preferences: [],
      verificationCode: '',
      country: country,
      language: language,
    }
  }

  /**
   * Registers a new user with Salesforce and Auth0.
   *
   * @async
   * @throws {Error} If registration fails.
   *
   * @remarks
   * - Ensures Salesforce ID is available
   * - Capitalizes names
   * - Handles various user preferences and authorizations
   */
  const registerUser = async () => {
    const user = registrationUser.value

    // Ensure Salesforce ID is available
    if (!salesforceId.value && !salesforceId.value.length) {
      const { salesforceId: sfId } = await $fetch('/api/auth0/getSfUser', {
        method: 'POST',
        body: {
          email: registrationUser.value.email,
        },
      })
      salesforceId.value = sfId ?? ''
    }

    await $fetch('/api/auth0/registerUser', {
      method: 'POST',
      body: {
        salesforceId: salesforceId.value,
        email: user.email,
        password: user.password,
        brandPreference: brandPreference.value as string[],
        gender: user.gender ?? 'unknown',
        prefix: user.prefix ?? '',
        lastName: capitalize(user.last_name),
        firstName: capitalize(user.first_name),
        dateOfBirth: encodeDate(user.date_of_birth, country),
        phoneNumber: user.phone_number
          ? user.phone_number.toString()
          : undefined,
        channelsPreference: user.channels_preferences,
        contactAuthorization: user.contact_authorization ?? false,
        marketingAuthorization: user.marketing_authorization ?? false,
        country: country ?? '',
        language: language,
        profilingConsent: user.profiling_consent ?? false,
      } satisfies z.infer<typeof registrationFormSchema>,
    })
      .catch(e => {
        throw e
      })
      .then(() => {
        registrationSuccessful.value = true
      })
  }

  /**
   * Checks if a user already exists on Salesforce.
   *
   * @async
   * @returns {Promise<boolean>} Whether the user exists on Salesforce.
   */
  const userAlreadyOnSf = async () => {
    const { sfExists } = await emailExists(registrationUser.value.email)
    return sfExists
  }

  const isSignupPage = route.fullPath.includes(signupPage)

  return {
    REGISTRATION_FORM_CODE_ID,
    REGISTRATION_FORM_PERSONAL_INFO_ID,
    registrationSuccessful,
    cleanRegistrationData,
    registrationUser,
    brandPreference,
    registerUser,
    isOtpValid,
    salesforceId,
    isSignupPage,
    userAlreadyOnSf,
  }
}
